/**
 * App-level listener for the "this write took the workspace over its
 * storage limit" signal raised by `apiClient` (see
 * `src/api/storage-limit-signal.ts`). Mounted once, in the authenticated
 * shell, so every upload surface — card description and Attachments,
 * comments, wiki embeds and file nodes, chat, client documents — plus wiki
 * duplicate, wiki paste and the copy-on-save paths all get the same message
 * without each one having to wire it.
 *
 * Wording follows the over-limit banner's audience (`isStorageAudience`):
 * owners and admins are told the workspace is over; everyone else is told
 * to contact their workspace admin. The "Manage storage" action is a
 * separate gate — only `manage-org` holders, who can actually open
 * Settings → My Organization, get it.
 *
 * The server stamps the over-limit state and sends the over-limit
 * notification at the moment of crossing, so invalidating the organization
 * and notifications queries makes the banner and the bell catch up at once.
 */

import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from '@tanstack/react-router'
import type { TFunction } from 'i18next'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'

import { subscribeStorageWentOverLimit } from '@/api/storage-limit-signal'
import { useToast } from '@/components/ToastProvider'
import { useMe } from '@/features/me/use-me'
import { useTenantPermissions } from '@/features/me/use-tenant-permissions'
import { NOTIFICATIONS_QUERY_KEY } from '@/features/notifications/use-notifications'
import { isStorageAudience } from '@/features/tenant-settings/storage-audience'
import { ORGANIZATION_QUERY_KEY } from '@/features/tenant-settings/use-organization'

/** Longer than a normal toast: it explains a state the user has to act on. */
export const STORAGE_OVER_LIMIT_TOAST_MS = 12_000

export interface StorageOverLimitToastContent {
  message: string
  /** Present only for people who can open the storage settings. */
  actionLabel: string | null
}

export function buildStorageOverLimitToast(
  audience: { isStorageAudience: boolean; canManageOrg: boolean },
  t: TFunction<'uploads'>,
): StorageOverLimitToastContent {
  return {
    message: audience.isStorageAudience
      ? t('storageWentOverLimit.storageAdmin')
      : t('storageWentOverLimit.member'),
    actionLabel: audience.canManageOrg ? t('storageWentOverLimit.action') : null,
  }
}

export function useStorageOverLimitNotifier(): void {
  const { t } = useTranslation('uploads')
  const toast = useToast()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const { canManageOrg } = useTenantPermissions()
  const me = useMe()
  const audience = isStorageAudience(me.data)

  // The subscription is registered once; the handler reads the latest
  // values through a ref so a permission or locale change never needs a
  // resubscribe (which could drop a signal raised in between).
  const latest = useRef({ t, toast, queryClient, navigate, canManageOrg, audience })
  latest.current = { t, toast, queryClient, navigate, canManageOrg, audience }

  useEffect(
    () =>
      subscribeStorageWentOverLimit(() => {
        const { t, toast, queryClient, navigate, canManageOrg, audience } = latest.current
        void queryClient.invalidateQueries({ queryKey: ORGANIZATION_QUERY_KEY })
        void queryClient.invalidateQueries({ queryKey: NOTIFICATIONS_QUERY_KEY })
        const content = buildStorageOverLimitToast({ isStorageAudience: audience, canManageOrg }, t)
        toast.error(content.message, {
          durationMs: STORAGE_OVER_LIMIT_TOAST_MS,
          ...(content.actionLabel
            ? {
                action: {
                  label: content.actionLabel,
                  onClick: () => {
                    void navigate({ to: '/settings', search: { tab: 'my-organization' } })
                  },
                },
              }
            : {}),
        })
      }),
    [],
  )
}
